"use client";

import * as DialogPrimitive from "@radix-ui/react-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { AlertCircle, Check, Loader2, X } from "lucide-react";
import { useState } from "react";

interface ValidItem {
  sku: string;
  name: string;
}

interface ExcelUploadPreviewProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (items: Array<{ sku: string; name?: string }>) => Promise<void>;
  validItems: ValidItem[];
  invalidSkus: string[];
  duplicateSkus: string[];
}

export function ExcelUploadPreview({
  isOpen,
  onClose,
  onConfirm,
  validItems,
  invalidSkus,
  duplicateSkus,
}: ExcelUploadPreviewProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const totalParsed = validItems.length + invalidSkus.length + duplicateSkus.length;
  const newItems = validItems.filter(
    (item) => !duplicateSkus.includes(item.sku),
  );

  const handleConfirm = async () => {
    if (newItems.length === 0 || isSubmitting) return;
    setIsSubmitting(true);
    try {
      await onConfirm(
        newItems.map((item) => ({ sku: item.sku, name: item.name })),
      );
      onClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <DialogPrimitive.Root
      open={isOpen}
      onOpenChange={(open) => {
        if (!open && !isSubmitting) onClose();
      }}
    >
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          className={cn(
            "fixed inset-0 z-[60] bg-black/80",
            "data-[state=open]:animate-in data-[state=closed]:animate-out",
            "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
          )}
        />
        <DialogPrimitive.Content
          className={cn(
            "fixed left-[50%] top-[50%] z-[60] translate-x-[-50%] translate-y-[-50%]",
            "w-[95vw] max-w-2xl max-h-[85vh]",
            "grid gap-4 border bg-white p-6 shadow-lg duration-200 sm:rounded-lg",
            "overflow-hidden flex flex-col",
            "data-[state=open]:animate-in data-[state=closed]:animate-out",
            "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
            "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
          )}
        >
          <div className="flex-shrink-0 flex flex-col space-y-1.5 text-left">
            <DialogPrimitive.Title className="text-lg font-semibold leading-none tracking-tight">
              Pré-visualização da Importação
            </DialogPrimitive.Title>
            <DialogPrimitive.Description className="text-sm text-muted-foreground">
              {totalParsed} item(ns) encontrado(s) no arquivo Excel
            </DialogPrimitive.Description>
          </div>

          <div className="flex-1 overflow-y-auto space-y-5 pr-1 min-h-0">
            <div className="grid grid-cols-3 gap-3">
              <div className="border rounded-lg p-3 text-center">
                <div className="flex items-center justify-center gap-1.5 mb-1">
                  <Check className="h-4 w-4 text-green-600" />
                  <span className="text-sm font-medium">Válidos</span>
                </div>
                <span className="text-2xl font-bold">{newItems.length}</span>
              </div>
              <div className="border rounded-lg p-3 text-center">
                <div className="flex items-center justify-center gap-1.5 mb-1">
                  <X className="h-4 w-4 text-destructive" />
                  <span className="text-sm font-medium">Inválidos</span>
                </div>
                <span className="text-2xl font-bold">{invalidSkus.length}</span>
              </div>
              <div className="border rounded-lg p-3 text-center">
                <div className="flex items-center justify-center gap-1.5 mb-1">
                  <AlertCircle className="h-4 w-4 text-yellow-600" />
                  <span className="text-sm font-medium">Duplicados</span>
                </div>
                <span className="text-2xl font-bold">{duplicateSkus.length}</span>
              </div>
            </div>

            {newItems.length > 0 && (
              <div className="space-y-2">
                <p className="text-sm font-medium">
                  Itens válidos a adicionar ({newItems.length})
                </p>
                <div className="border rounded-lg max-h-[200px] overflow-y-auto">
                  <div className="space-y-0.5 p-2">
                    {newItems.slice(0, 100).map((item) => (
                      <div
                        key={item.sku}
                        className="flex items-center gap-2 text-sm py-1.5 px-2 rounded hover:bg-muted/50"
                      >
                        <Check className="h-3 w-3 text-green-600 flex-shrink-0" />
                        <span className="font-mono font-medium">{item.sku}</span>
                        {item.name && (
                          <span className="text-muted-foreground truncate">
                            — {item.name}
                          </span>
                        )}
                      </div>
                    ))}
                    {newItems.length > 100 && (
                      <p className="text-xs text-muted-foreground text-center py-2">
                        ...e mais {newItems.length - 100} itens
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {invalidSkus.length > 0 && (
              <div className="space-y-2">
                <p className="text-sm font-medium">
                  SKUs não encontrados em ITE001 ({invalidSkus.length})
                </p>
                <div className="border rounded-lg max-h-[120px] overflow-y-auto">
                  <div className="flex flex-wrap gap-1.5 p-2">
                    {invalidSkus.map((sku) => (
                      <Badge
                        key={sku}
                        variant="destructive"
                        className="font-mono text-xs"
                      >
                        {sku}
                      </Badge>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {duplicateSkus.length > 0 && (
              <div className="space-y-2">
                <p className="text-sm font-medium">
                  Já adicionados na lista ({duplicateSkus.length})
                </p>
                <div className="border rounded-lg max-h-[120px] overflow-y-auto">
                  <div className="flex flex-wrap gap-1.5 p-2">
                    {duplicateSkus.map((sku) => (
                      <Badge
                        key={sku}
                        variant="outline"
                        className="font-mono text-xs"
                      >
                        {sku}
                      </Badge>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="flex flex-col-reverse md:flex-row md:justify-end gap-2 pt-4 border-t flex-shrink-0">
            <Button
              variant="outline"
              onClick={onClose}
              disabled={isSubmitting}
              className="w-full md:w-auto"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleConfirm}
              disabled={newItems.length === 0 || isSubmitting}
              className="w-full md:w-auto"
            >
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Adicionar {newItems.length} item(ns) válido(s)
            </Button>
          </div>

          <DialogPrimitive.Close
            className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none"
            disabled={isSubmitting}
          >
            <X className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
